import { useCallback } from "react";

import useArticleSection from "@FillInTableContainer/hooks/useArticleSection";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { ArticleEntryData } from "@/types/articleEntryData.types";
import type {
  DrugProfileValue,
  EntryDataValueAndOptions,
} from "@/types/drugProfile.types";

interface UseHandleGroupShellReturn {
  handleChangeDrugProfileGroupShell: (value: string) => void;
  isDuplicatedValue: (value: string) => boolean;
}

interface Props {
  positionPropsInfo: {
    groupTableKey: "Drug Profile";
    groupTableIndex: number;
    groupShellKey: string | null;
    groupShellIndex: number | null;
    tableKey: string | null;
    tableIndex: number;
  };
}

export const useChangeDrugProfileGroupShell = ({
  positionPropsInfo,
}: Props): UseHandleGroupShellReturn => {
  const { groupShellKey, tableIndex } = positionPropsInfo;
  const articleSection = useArticleSection();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());

  const isDuplicatedValue = useCallback(
    (value: string) => {
      if (!entryData || !groupShellKey) return false;

      const drugProfileGroupShell = entryData[articleSection]?.[
        "Drug Profile"
      ] as DrugProfileValue[];

      if (!drugProfileGroupShell) return false;

      const shell = drugProfileGroupShell[tableIndex];
      if (!shell) return false;

      const key = groupShellKey as keyof DrugProfileValue;
      const currentValue = shell[key] as EntryDataValueAndOptions;

      if (!currentValue?.value) return false;

      const currentValues = currentValue.value.map((v) =>
        typeof v === "string" ? v : v.text,
      );
      const newValues = value.split("__").map((v) => v.trim());

      if (currentValues.length !== newValues.length) return false;

      return currentValues.every((v, i) => v === newValues[i]);
    },
    [entryData, groupShellKey, articleSection, tableIndex],
  );

  const handleChangeDrugProfileGroupShell = useCallback(
    (value: string) => {
      if (!entryData) return;

      const updatedEntryData = { ...entryData } as ArticleEntryData;
      const drugProfileGroupShell = updatedEntryData[articleSection]?.[
        "Drug Profile"
      ] as DrugProfileValue[];

      if (drugProfileGroupShell && groupShellKey) {
        const updatedShells = drugProfileGroupShell.map((shell, index) => {
          if (index === tableIndex) {
            const key = groupShellKey as keyof DrugProfileValue;
            const currentValue = shell[key] as EntryDataValueAndOptions;

            if (currentValue) {
              if (groupShellKey === "Generic / Biosimilar") {
                return {
                  ...shell,
                  [key]: {
                    ...currentValue,
                    value: [value === "true" ? "true" : "false"],
                    options: currentValue.options || [],
                  } as any,
                };
              }

              if (!value.trim()) {
                return {
                  ...shell,
                  [key]: {
                    ...currentValue,
                    value: [],
                    options: currentValue.options || [],
                  } as EntryDataValueAndOptions,
                };
              }

              const values = value.split("__").map((text) => ({
                text: text.trim(),
                is_modified: true,
              }));

              if (groupShellKey === "INN Name" && values.length > 3) {
                return shell;
              }

              return {
                ...shell,
                [key]: {
                  ...currentValue,
                  value: values,
                  options: currentValue.options || [],
                } as EntryDataValueAndOptions,
              };
            }
          }
          return shell;
        });

        (updatedEntryData[articleSection] as any) = {
          ...updatedEntryData[articleSection],
          "Drug Profile": updatedShells,
        };
      }

      setEntryData(updatedEntryData);
    },
    [entryData, setEntryData, groupShellKey, articleSection, tableIndex],
  );

  return {
    handleChangeDrugProfileGroupShell,
    isDuplicatedValue,
  };
};
